import React, { useEffect, useState } from 'react'
import { Tab, Button, SelectTabData } from '@fluentui/react-components'
import {
   InfoShield20Regular,
   SearchShield20Regular,
   Scales20Regular,
   TaskListSquareLtr20Regular,
   LockClosed24Regular,
   InfoShield20Filled,
   Scales20Filled,
   SearchShield20Filled,
   TaskListSquareLtr20Filled,
   LockClosed20Regular,
} from '@fluentui/react-icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from '../../../i18n/i18n'
import { Layout } from '../../../pages/Layout'
import { SWOTIdentifying } from './SWOTIdentifying'
import { SWOTNavLinks } from '../../common/navigation/SWOTNavLinks'
import { SWOTTabs } from '../../../helpers/swotHelper'
import { SWOTAnalyseEvaluate } from './SWOTAnalyseEvaluate'
import { SWOTActionPlans } from './SWOTActionPlans'
import { ActionPlanStatus, RiskAnalysis, RiskAnalysisStatus } from '../../../api/schemas/schema'
import { ModalCompleteRiskAssessment } from '../ModalCompleteRiskAssessment'
import { RiskInformation } from '../RiskInformation'
import { IItemState } from '../../../interfaces/IItemState'
import { NavigationLink } from '../../../interfaces/INavigationLink'

type SWOTAnalysisProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
}

export const SWOTAnalysis = ({ setRiskAnalysisState, analysisState }: SWOTAnalysisProps) => {
   const [searchParams, setSearchParams] = useSearchParams()
   const selectedTab = searchParams.get('tab')
   const navigate = useNavigate()
   const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false)
   const { item: analysis } = analysisState

   useEffect(() => {
      if (searchParams.get('tab') === null) {
         setSearchParams((prev) => ({ ...prev, tab: 'info' }))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchParams])

   const getSWOTTitle = (tab: string): string => {
      switch (tab) {
         case SWOTTabs.Info:
            return t('Information')
         case SWOTTabs.Identifying:
            return t('Identify')
         case SWOTTabs.AnalyseEvaluate:
            return t('AnalyseEvaluate')
         case SWOTTabs.ActionPlan:
            return t('RiskManagementPlan')
      }
   }
   const completeBtnDisabled = analysis.status === RiskAnalysisStatus.Completed || analysis.actionPlans.some((ap) => ap.status === ActionPlanStatus.NotDecided)
   return (
      <Layout
         breadCrumbItems={[
            { path: NavigationLink.RiskManagement, active: false, title: t('RiskManagement') },
            { path: `${NavigationLink.RiskManagement}/${analysis.id}`, active: true, title: analysis.name },
         ]}
         activeDesktopLink={NavigationLink.RiskManagement}
         activePhoneTab={`${NavigationLink.RiskManagement}/${analysis.id}?tab=${selectedTab}`}
         phoneNavLinks={
            <SWOTNavLinks
               id={analysisState?.item?.id ?? ''}
               active={selectedTab}
               actionTab={<Tab value={'complete'} icon={<LockClosed24Regular />} disabled={completeBtnDisabled} />} // Skriv logik sen för att denna ska vara av/på enligt ticket
            />
         }
         customOnTabSelect={(e, data: SelectTabData) => {
            if (data.value === 'complete') setCompleteModalOpen(true)
            else navigate(data.value)
         }}
         button={
            <Button disabled={completeBtnDisabled} icon={<LockClosed20Regular />} onClick={() => setCompleteModalOpen(true)} appearance="transparent">
               {t('CompleteRiskAssessment')}
            </Button>
         }
         tabs={
            <>
               <Tab icon={selectedTab === SWOTTabs.Info ? <InfoShield20Filled /> : <InfoShield20Regular />} value={SWOTTabs.Info}>
                  {getSWOTTitle(SWOTTabs.Info)}
               </Tab>
               <Tab icon={selectedTab === SWOTTabs.Identifying ? <SearchShield20Filled /> : <SearchShield20Regular />} value={SWOTTabs.Identifying}>
                  {getSWOTTitle(SWOTTabs.Identifying)}
               </Tab>
               <Tab icon={selectedTab === SWOTTabs.AnalyseEvaluate ? <Scales20Filled /> : <Scales20Regular />} value={SWOTTabs.AnalyseEvaluate}>
                  {getSWOTTitle(SWOTTabs.AnalyseEvaluate)}
               </Tab>
               <Tab icon={selectedTab === SWOTTabs.ActionPlan ? <TaskListSquareLtr20Filled /> : <TaskListSquareLtr20Regular />} value={SWOTTabs.ActionPlan}>
                  {getSWOTTitle(SWOTTabs.ActionPlan)}
               </Tab>
            </>
         }
         overflowAuto={true}
      >
         {selectedTab === SWOTTabs.Info && <RiskInformation analysisState={analysisState} setAnalysisState={setRiskAnalysisState} />}
         {selectedTab === SWOTTabs.Identifying && <SWOTIdentifying analysisState={analysisState} setRiskAnalysisState={setRiskAnalysisState} />}
         {selectedTab === SWOTTabs.AnalyseEvaluate && <SWOTAnalyseEvaluate analysisState={analysisState} setRiskAnalysisState={setRiskAnalysisState} />}
         {selectedTab === SWOTTabs.ActionPlan && <SWOTActionPlans analysisState={analysisState} setRiskAnalysisState={setRiskAnalysisState} />}
         <ModalCompleteRiskAssessment
            open={completeModalOpen}
            close={() => setCompleteModalOpen(false)}
            riskAnalysisState={analysisState}
            setRiskAnalysisState={setRiskAnalysisState}
         />
      </Layout>
   )
}
